import ArrowDownward from "@mui/icons-material/ArrowDownward";
import Check from "@mui/icons-material/Check";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Clear from "@mui/icons-material/Clear";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import SaveAlt from "@mui/icons-material/SaveAlt";
import Search from "@mui/icons-material/Search";
import { TablePagination, ThemeProvider, createTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MaterialTable, { MTableToolbar } from "material-table";
import moment from "moment";
import React, { Component, forwardRef } from "react";
import { Modal } from "react-bootstrap";
import Clamp from "react-multiline-clamp";
import CommonApi from "../../api/CommonApi";
import downloadImage from "../../assets/images/download .png";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import Papa from "papaparse";

const tableIcons = {
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Filter: () => <></>,
};

class AutomationDashboard extends Component {
  constructor(props) {
    super(props);
    if (this.props.onRef) {
      this.props.onRef(this);
    }
    this.state = {
      isLoading: true,
      modalData: {},
      downloadDataToCsv: true,
      page: 0,
      pageSize: 10,
      lastEvaluatedKey: null,
      keyHistory: [null],
      selectedRows: [],
      isZipping: false,
    };
  }

  componentDidMount = async () => {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    console.log("AutomationDashboard props: ", this.props.sessionData);
    await this.fetchAutomationData();
  };

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }

  handleBeforeUnload = (e) => {
    if (this.state.isZipping) {
      e.preventDefault();
      e.returnValue =
        "If you reload, auto-refresh will not happen and you may have to refresh manually to see the latest data.";
      return e.returnValue;
    }
  };

  mapAutomationData = (list) => {
    return list.map((item) => ({
      uploadDate: item.uploadedTimestamp
        ? moment(Number(item.uploadedTimestamp)).format("M/D/YY")
        : "-",
      clientId: item.client_id || "-",
      username: item.username || item.modifiedBy?.split("@")[0] || "-",
      fileName: item.fileName || "N/A",
      status: item.status,
      invoiceData: item.invoiceFile || item.workflowData?.excelFileBase64String,
      invoicePath: item.invoicePath,
      s3FilePath: item.s3_file_path,
      id: item.user_email_id,
      InvoiceGenerationError: item.InvoiceGenerationError,
      lastCheckDate: item.lastCheckDate || "-",
      validationErrors: item.validationErrors,
    }));
  };

  fetchAutomationData = async (incomingNextKey = null) => {
    this.setState({ isLoading: true });

    try {
      const res = await CommonApi.getAutomationData(incomingNextKey);
      console.log("AutomationDashboard data: ", res.list);
      console.table(res.list);
      const newList = res?.list || [];
      const mappedNewData = this.mapAutomationData(newList);

      // If it's the initial call (incomingNextKey is null), we replace the state to clear old data.
      // If it's a recursive call (loadAll=true), the API returns the full list, so we replace the partial list.
      this.props.updateTabRowData(this.props.tabName, mappedNewData);

      if (!incomingNextKey && res.nextKey) {
        await this.fetchAutomationData(res.nextKey);
      } else {
        this.setState({ isLoading: false });
      }
    } catch (error) {
      console.error("Error in recursive fetch:", error);
      this.setState({ isLoading: false });
    }
  };

  setModalData = (data) => {
    this.setState({ modalData: data });
  };

  /**
   * Generic helper: get a signed URL for any S3 path and trigger a browser download.
   * @param {string} s3Path   - the S3 key / path (passed as s3_file_path to the API)
   * @param {string} fileName - the name to give the downloaded file
   */
  handleDownloadViaSignedUrl = async (s3Path, fileName) => {
    try {
      this.setState({ isLoading: true });
      const res = await CommonApi.getS3SignedUrl({ s3Path });
      const downloadURL = res?.url || res?.signedUrl || res?.downloadUrl;

      if (downloadURL) {
        const response = await fetch(downloadURL);
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }
      this.setState({ isLoading: false });
    } catch (error) {
      console.error("Error downloading file", error);
      this.setState({ isLoading: false });
    }
  };

  handleDownloadInvoice = async (rowData) => {
    const invoiceName = (rowData.clientId || "Client") + "_Invoice.pdf";
    await this.handleDownloadViaSignedUrl(rowData.invoicePath, invoiceName);
  };

  handleSelectionChange = (rows) => {
    this.setState({ selectedRows: rows });
  };

  handleBulkDownload = async () => {
    const { selectedRows } = this.state;
    const filesToZip = selectedRows
      .filter((row) => row.invoicePath)
      .map((row) => {
        return {
          s3Path: row.invoicePath,
          zipFileName: `${row.clientId}_Invoice.pdf`,
        };
      });

    if (filesToZip.length === 0) {
      alert("Selected rows do not have invoice files.");
      return;
    }

    this.setState({ isZipping: true });

    try {
      const initRes = await CommonApi.initiateBulkDownload({
        files: filesToZip,
      });
      const triggerId = initRes.trigger_id;

      this.pollZipStatus(triggerId);
    } catch (error) {
      console.error("Bulk download error", error);
      this.setState({ isZipping: false });
    }
  };

  pollZipStatus = async (triggerId) => {
    let attempts = 0;
    const maxAttempts = 12;

    const poll = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(poll);
        this.setState({ isZipping: false });
        alert("Zip generation timed out. Please try again.");
        return;
      }

      try {
        const res = await CommonApi.getBulkDownloadStatus({
          trigger_id: triggerId,
        });

        if (res.status === "completed") {
          clearInterval(poll);
          this.setState({ isZipping: false }, async () => {
              const response = await fetch(res.downloadUrl);
              const blob = await response.blob();
              const objectUrl = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = objectUrl;
              link.setAttribute("download", "Bulk Invoice.zip");
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(objectUrl);
          });
        } else if (res.status === "failed") {
          clearInterval(poll);
          this.setState({ isZipping: false });
          alert("Failed to generate zip. Please try again.");
        }
      } catch (e) {
        clearInterval(poll);
        this.setState({ isZipping: false });
      }
    }, 5000); // 5 sec
  };

  dateSort = (a, b, field) => {
    let momentA = moment(a[field], "M/D/YY");
    let momentB = moment(b[field], "M/D/YY");

    if (
      (!momentA.isValid() && momentB.isValid()) ||
      momentA.isBefore(momentB)
    ) {
      return -1;
    } else if (
      (momentA.isValid() && !momentB.isValid()) ||
      momentA.isAfter(momentB)
    ) {
      return 1;
    } else {
      return 0;
    }
  };

  handleExportCSV = () => {
    const { rowDataList } = this.props;
    const { selectedRows } = this.state;
    
    const dataToExport = selectedRows && selectedRows.length > 0 ? selectedRows : rowDataList;

    if (!dataToExport || dataToExport.length === 0) {
      alert("No data available to export");
      return;
    }

    const exportData = dataToExport.map((row) => ({
      "Upload Date": row.uploadDate,
      "Client ID": row.clientId,
      Username: row.username,
      Filename: row.fileName,
      Status: row.status,
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Automation_Dashboard_${moment().format("MM_DD_YYYY")}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { rowDataList, tabName } = this.props;
    const { isLoading, modalData, selectedRows, isZipping } = this.state;
    const defaultMaterialTheme = createTheme();

    const columns = [
      {
        title: "Upload Date",
        field: "uploadDate",
        // defaultSort: "desc",
        customSort: (a, b) => this.dateSort(a, b, "uploadDate"),
        headerStyle: { width: "120px" },
        cellStyle: { width: "120px" },
        filterPlaceholder: "Filter",
      },
      {
        title: "Client ID",
        field: "clientId",
        render: (rowData) => rowData.clientId,
        headerStyle: { width: "120px" },
        cellStyle: { width: "120px" },
        filterPlaceholder: "Filter",
      },
      {
        title: "Last Check Date",
        field: "lastCheckDate",
        render: (rowData) => rowData.lastCheckDate,
        headerStyle: { width: "120px" },
        cellStyle: { width: "120px" },
        filterPlaceholder: "Filter",
      },
      {
        title: "Filename",
        field: "fileName",
        render: (rowData) => (
          <Clamp lines={1}>
            {rowData.s3FilePath ? (
              <span
                role="button"
                tabIndex={0}
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
                onClick={() =>
                  this.handleDownloadViaSignedUrl(
                    rowData.s3FilePath,
                    rowData.fileName,
                  )
                }
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  this.handleDownloadViaSignedUrl(
                    rowData.s3FilePath,
                    rowData.fileName,
                  )
                }
              >
                {rowData.fileName}
              </span>
            ) : (
              rowData.fileName
            )}
          </Clamp>
        ),
        headerStyle: { width: "200px" },
        cellStyle: { width: "200px" },
        filterPlaceholder: "Filter",
      },
      {
        title: "Status",
        field: "status",
        render: (rowData) => {
          let color = "black";
          if (rowData.status === "Success") {
            color = "#1bd530";
          } else if (
            rowData.status === "Faliure" ||
            rowData.status === "Failure"
          ) {
            color = "#de6060";
          } else if (rowData.status === "In Progress") {
            color = "#ffaa0b";
          }
          return (
            <span style={{ color, fontWeight: "600" }}>{rowData.status}</span>
          );
        },
        filterPlaceholder: "Filter",
        headerStyle: { width: "120px" },
        cellStyle: { width: "120px" },
      },
      {
        title: "Username",
        field: "username",
        headerStyle: { width: "100px" },
        cellStyle: { width: "100px" },
        filterPlaceholder: "Filter",
      },
      {
        title: "Invoice",
        field: "invoice",
        sorting: false,
        headerStyle: { textAlign: "center", width: "50px" },
        cellStyle: { textAlign: "center", width: "50px" },
        render: (rowData) => {
          const hasValidationErrors = !!rowData.validationErrors;
          const hasGenError = !!rowData.InvoiceGenerationError;

          if (hasValidationErrors || hasGenError) {
            return (
              <Tooltip
                placement="left-start"
                title={
                  <span
                    style={{
                      color: "#b91c1c",
                      fontWeight: "500",
                      fontSize: "12px",
                    }}
                  >
                    {hasValidationErrors
                      ? rowData.validationErrors
                      : rowData.InvoiceGenerationError}
                  </span>
                }
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "white",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                      border: "1px solid #ffa0a0ff",
                      color: "red",
                      width: "400px",
                    },
                  },
                }}
              >
                <IconButton size="small">
                  <ErrorOutlineRoundedIcon style={{ color: "#ff6060" }} />
                </IconButton>
              </Tooltip>
            );
          }

          const canDownload = rowData.invoiceData || rowData.invoicePath;
          return (
            <button
              className="btn border-0"
              type="button"
              disabled={!canDownload}
              onClick={() => this.handleDownloadInvoice(rowData)}
            >
              <img
                src={downloadImage}
                alt="Download"
                style={{
                  width: "25px",
                  filter: canDownload
                    ? "grayscale(0)"
                    : "grayscale(100%) opacity(0.5)",
                }}
                className={!canDownload ? "disabledImage" : ""}
              />
            </button>
          );
        },
        filterPlaceholder: "Filter",
      },
    ];

    return (
      <div className="dashboardMaterialTable">
        <ThemeProvider theme={defaultMaterialTheme}>
          <MaterialTable
            icons={tableIcons}
            title=""
            columns={columns}
            data={rowDataList || []}
            isLoading={isLoading}
            options={{
              filtering: true,
              selection: true,
              showTextRowsSelected: false,
              headerSelectionProps: {
                color: "default",
                style: { color: "white" },
                sx: { color: "white" },
              },
              pageSize: 10,
              headerStyle: {
                backgroundColor: "#030043",
                color: "#FFF",
                fontFamily: "Montserrat",
              },
              rowStyle: {
                fontFamily: "Montserrat",
                fontSize: "0.95rem",
              },
              padding: "2px",
            }}
            onSelectionChange={this.handleSelectionChange}
            components={{
              Toolbar: (props) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    paddingRight: "10px",
                  }}
                >
                  {selectedRows.length > 1 && (
                    <button
                      className="btn btn-sm mx-2 shadow-none"
                      onClick={this.handleBulkDownload}
                      disabled={isZipping}
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        color: "#878788ff",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {isZipping ? (
                        "Preparing Zip..."
                      ) : (
                        <>
                          <SaveAlt style={{ fontSize: "20px" }} />
                          Export Invoices
                        </>
                      )}
                    </button>
                  )}
                  <MTableToolbar {...props} />
                  <Tooltip title="Export the dashboard data">
                    <IconButton onClick={this.handleExportCSV}>
                      <SaveAlt />
                    </IconButton>
                  </Tooltip>
                </div>
              ),
            }}
          />
        </ThemeProvider>

        <Modal
          show={modalData.showModel}
          onHide={() => this.setModalData({ showModel: false })}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{modalData.modalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modalData.modalContent}</Modal.Body>
        </Modal>
      </div>
    );
  }
}

export default AutomationDashboard;
